import is from '@sindresorhus/is';
import type { RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import type { PendingCommentTarget } from './active-thread-store';
import { PENDING_COMPOSER_ID } from './CommentComposer';
import type { LayoutItem } from './compute-layout';
import { computeLayout } from './compute-layout';
import { resolveContentRoot } from './resolve-content-root';
import { scrollOffsetForAncestor } from './scroll-anchor-into-view';
import type { Comment, CommentTarget } from './types';

const COMPOSER_FALLBACK_DELAY_MS = 320;
const RAIL_BOTTOM_PADDING_PX = 160;
const CARD_TRANSFORM_TRANSITION = 'transform 300ms cubic-bezier(0.165, 0.84, 0.44, 1)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

type UseCommentRailLayoutInput = {
    readonly itemId: string;
    readonly comments: readonly Comment[];
    readonly railContentRef: RefObject<HTMLElement | null>;
    readonly cardRefs: Map<string, HTMLElement>;
    readonly onLayoutBottom?: (bottom: number) => void;
};

const resolveAnchor = (
    contentEl: HTMLElement,
    itemId: string,
    comment: { readonly id: string; readonly target: CommentTarget },
): HTMLElement | null => {
    const target = comment.target;
    if (target.kind === 'title') {
        return contentEl.querySelector<HTMLElement>(`[data-anchor-kind="title"][data-item-id="${itemId}"]`);
    }
    if (target.kind === 'skill') {
        return contentEl.querySelector<HTMLElement>(`[data-anchor-kind="skill"][data-skill-id="${target.skillId}"]`);
    }
    return contentEl.querySelector<HTMLElement>(`mark[data-comment-id="${comment.id}"]`);
};

const buildLayoutItems = (
    comments: readonly Comment[],
    contentEl: HTMLElement,
    railTop: number,
    railPageY: number,
    cardRefs: Map<string, HTMLElement>,
    itemId: string,
    pendingTarget: PendingCommentTarget | null,
    lastKnownIdeals: Map<string, number>,
): readonly LayoutItem[] => {
    const items: LayoutItem[] = [];
    const anchorables: readonly { readonly id: string; readonly target: CommentTarget }[] = comments.map((comment) => ({
        id: comment.id,
        target: comment.target,
    }));
    for (const anchorable of anchorables) {
        const cardEl = cardRefs.get(anchorable.id);
        if (is.undefined(cardEl)) continue;
        const anchorEl = resolveAnchor(contentEl, itemId, anchorable);
        const ideal = is.null(anchorEl)
            ? (lastKnownIdeals.get(anchorable.id) ?? 0)
            : anchorEl.getBoundingClientRect().top - railTop;
        if (!is.null(anchorEl)) {
            lastKnownIdeals.set(anchorable.id, ideal);
        }
        items.push({ id: anchorable.id, ideal, height: cardEl.getBoundingClientRect().height });
    }
    const hasPendingComment = !is.null(pendingTarget) && items.some((item) => item.id === pendingTarget.commentId);
    if (!is.null(pendingTarget) && !hasPendingComment) {
        const composerEl = cardRefs.get(PENDING_COMPOSER_ID);
        items.push({
            id: PENDING_COMPOSER_ID,
            ideal: pendingTarget.anchorPageY - railPageY,
            height: is.undefined(composerEl) ? 0 : composerEl.getBoundingClientRect().height,
        });
    }
    return items.slice().sort((a, b) => a.ideal - b.ideal);
};

const hasTransitionDuration = (duration: string): boolean => {
    const trimmed = duration.trim();
    return trimmed !== '0s' && trimmed !== '0ms';
};

const resolveCardTransition = (card: HTMLElement): string => {
    const computedStyle = window.getComputedStyle(card);
    const hasExistingTransition = computedStyle.transitionDuration.split(',').some(hasTransitionDuration);
    if (hasExistingTransition) return computedStyle.transition;
    if (is.function(window.matchMedia) && window.matchMedia(REDUCED_MOTION_QUERY).matches) return 'none';
    return CARD_TRANSFORM_TRANSITION;
};

const applyCardTransforms = (
    items: readonly LayoutItem[],
    cardRefs: Map<string, HTMLElement>,
    placed: ReadonlyMap<string, number>,
    positionedCards: WeakSet<HTMLElement>,
    scheduleTransitionRestore: (card: HTMLElement, transition: string) => void,
): number => {
    let maxBottom = 0;
    for (const item of items) {
        const card = cardRefs.get(item.id);
        if (is.undefined(card)) continue;
        const top = placed.get(item.id) ?? 0;
        const transform = `translate3d(0, ${top}px, 0)`;
        if (!positionedCards.has(card)) {
            positionedCards.add(card);
            const transition = resolveCardTransition(card);
            card.style.transition = 'none';
            card.style.transform = transform;
            card.getBoundingClientRect();
            scheduleTransitionRestore(card, transition);
        } else {
            card.style.transition = resolveCardTransition(card);
            card.style.transform = transform;
        }
        const bottom = top + item.height;
        if (bottom > maxBottom) maxBottom = bottom;
    }
    return maxBottom;
};

export const useCommentRailLayout = ({
    itemId,
    comments,
    railContentRef,
    cardRefs,
    onLayoutBottom,
}: UseCommentRailLayoutInput): void => {
    const activeThreadId = useActiveThreadStore((state) => state.activeThreadId);
    const pendingTarget = useActiveThreadStore((state) => state.pendingTarget);

    const positionedCardsRef = useRef<WeakSet<HTMLElement>>(new WeakSet());
    const transitionRestoreFrameRefs = useRef<Set<number>>(new Set());
    const lastKnownIdealsRef = useRef<Map<string, number>>(new Map());

    const scheduleTransitionRestore = useCallback((card: HTMLElement, transition: string) => {
        let frame = 0;
        frame = window.requestAnimationFrame(() => {
            transitionRestoreFrameRefs.current.delete(frame);
            if (card.isConnected) {
                card.style.transition = transition;
            }
        });
        transitionRestoreFrameRefs.current.add(frame);
    }, []);

    const doLayout = useCallback(() => {
        const railContentEl = railContentRef.current;
        const contentEl = resolveContentRoot(itemId);
        if (is.null(railContentEl) || is.null(contentEl)) return;

        const railTop = railContentEl.getBoundingClientRect().top;
        const railPageY = railTop + scrollOffsetForAncestor(railContentEl);
        const items = buildLayoutItems(
            comments,
            contentEl,
            railTop,
            railPageY,
            cardRefs,
            itemId,
            pendingTarget,
            lastKnownIdealsRef.current,
        );
        const effectiveActiveId =
            !is.null(activeThreadId) && items.some((item) => item.id === activeThreadId) ? activeThreadId : null;
        const hasPendingComment = !is.null(pendingTarget) && items.some((item) => item.id === pendingTarget.commentId);
        const pivotId =
            is.null(effectiveActiveId) && !is.null(pendingTarget) && !hasPendingComment
                ? PENDING_COMPOSER_ID
                : effectiveActiveId;
        const viewportPivotIdx = items.findIndex((it) => it.ideal >= 0);
        const placed = computeLayout(items, pivotId, viewportPivotIdx);
        const maxBottom = applyCardTransforms(
            items,
            cardRefs,
            placed,
            positionedCardsRef.current,
            scheduleTransitionRestore,
        );
        onLayoutBottom?.(maxBottom);
        const articleHeight = contentEl.getBoundingClientRect().height;
        const desiredMinHeight = Math.round(Math.max(maxBottom + RAIL_BOTTOM_PADDING_PX, articleHeight));
        const desired = `${desiredMinHeight}px`;
        if (railContentEl.style.minHeight !== desired) {
            railContentEl.style.minHeight = desired;
        }
    }, [
        itemId,
        comments,
        activeThreadId,
        pendingTarget,
        railContentRef,
        cardRefs,
        onLayoutBottom,
        scheduleTransitionRestore,
    ]);

    const layoutRef = useRef<() => void>(doLayout);
    layoutRef.current = doLayout;

    useLayoutEffect(() => {
        doLayout();
        const handle = window.setTimeout(() => layoutRef.current(), COMPOSER_FALLBACK_DELAY_MS);
        return () => {
            window.clearTimeout(handle);
        };
    }, [doLayout]);

    const scheduledRafRef = useRef(0);
    const scheduleLayout = useCallback(() => {
        if (scheduledRafRef.current !== 0) return;
        scheduledRafRef.current = window.requestAnimationFrame(() => {
            scheduledRafRef.current = 0;
            layoutRef.current();
        });
    }, []);

    useEffect(() => {
        const railContentEl = railContentRef.current;
        const contentEl = resolveContentRoot(itemId);
        if (is.null(railContentEl) || is.null(contentEl)) return;
        railContentEl.style.minHeight = '';
        doLayout();
        const observer = new ResizeObserver(scheduleLayout);
        observer.observe(contentEl);
        for (const card of cardRefs.values()) {
            observer.observe(card);
        }
        const mutationObserver = new MutationObserver(scheduleLayout);
        mutationObserver.observe(contentEl, { childList: true, characterData: true, subtree: true });
        return () => {
            observer.disconnect();
            mutationObserver.disconnect();
        };
    }, [itemId, doLayout, scheduleLayout, railContentRef, cardRefs]);

    useEffect(
        () => () => {
            for (const frame of transitionRestoreFrameRefs.current) {
                window.cancelAnimationFrame(frame);
            }
            transitionRestoreFrameRefs.current.clear();
        },
        [],
    );
};
